// ISDP CUSTOM: Release-scope flags — the single source of truth for which optional surfaces
// a deployment ships. Read at RUNTIME from config (`devhub.features.*`, declared frontend-
// visible in ../config.d.ts), so the ONE promoted image can behave differently per
// environment.
//
// They were build-time constants until 2026-09-14. That made staging and production — which
// run the same image by design, so that prod runs exactly what staging tested — unable to
// disagree. The moment stakeholders needed the Partner Portal visible on staging while
// production kept it hidden, constants could not express it.
//
// Set per environment, and nothing else:
//   local     app-config.yaml               → partnerPortal: true   (so it can be worked on)
//   staging   DEVHUB_FEATURE_PARTNER_PORTAL → true                  (stakeholder review)
//   prod      DEVHUB_FEATURE_PARTNER_PORTAL → false                 (out of the 1.0 release)
//
// TURNING A SURFACE ON EVERYWHERE is one env var on the Container App — no rebuild, no
// deploy, no code change. RETIRING A FLAG for good is mechanical: each flag's doc comment
// below lists every site that reads it, so delete those branches, the key in ../config.d.ts,
// and the helper. Keep those lists accurate — they are what makes removal safe.

/** The optional surfaces a deployment can switch on. */
export type DevHubFeature = 'partnerPortal' | 'partnerDocBodies';

/**
 * Config values arrive as a real boolean from a YAML file but as a STRING from a container
 * env var, so both are accepted. Anything else — absent, empty, malformed — is false: the
 * flags fail CLOSED, so a missing value can never expose a surface by accident.
 */
function coerce(value: unknown): boolean {
  return value === true || value === 'true' || value === '1';
}

/**
 * The slice of Backstage's `Config` this needs. Declared structurally rather than importing
 * @backstage/config, so this module stays dependency-free for every consumer (backend
 * plugins, frontend components via configApiRef, and tests passing a stub).
 */
export interface FeatureConfig {
  getOptional(key: string): unknown;
}

/**
 * Is a surface part of this deployment?
 *
 * `partnerPortal` gates:
 *   - packages/app/src/index.tsx                              — anonymous PublicApp bootstrap
 *   - packages/app/src/App.tsx                                — the external-world branch
 *   - packages/app/src/components/Root/ProfileDropdown.tsx    — "View Partner Portal"
 *   - packages/app/src/components/Root/Root.tsx               — sign-out destination
 *   - packages/app/src/components/auth/useUserRole.ts         — internal "preview as" read
 *   - packages/backend/src/modules/public/publicModule.ts     — unauthenticated /api/public/*
 *
 * `partnerDocBodies` gates:
 *   - packages/backend/src/modules/public/productDocsRouter.ts — whether an ANONYMOUS caller
 *     receives public-tier tab bodies. Off = anonymous callers get no tabs at all, so nothing
 *     repo-derived reaches the DOM. Signed-in callers are unaffected: internal accounts see
 *     everything, entitled partners keep their tier. See config.d.ts.
 */
export function featureEnabled(
  config: FeatureConfig | undefined,
  feature: DevHubFeature,
): boolean {
  try {
    return coerce(config?.getOptional(`devhub.features.${feature}`));
  } catch {
    // A malformed config must not take a page down; treat it as off.
    return false;
  }
}

/**
 * Same answer, for code that runs BEFORE React mounts and so has no configApi — specifically
 * index.tsx, which must decide whether to boot the anonymous Partner Portal root or the main
 * app. Both the deployed app-backend and the local CLI dev server inject the frontend-visible
 * config into index.html as a single `<script type="backstage.io/config">` block (verified on
 * both), which is the same data the ConfigReader consumes, so this reads it directly.
 */
export function featureEnabledFromInjectedConfig(feature: DevHubFeature): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const el = document.querySelector('script[type="backstage.io/config"]');
    if (!el?.textContent) return false;
    // The block is an array of { context, data } layers, merged later-wins by Backstage.
    const layers = JSON.parse(el.textContent) as Array<{ data?: unknown }>;
    let found: unknown;
    for (const layer of layers) {
      const v = (layer?.data as any)?.devhub?.features?.[feature];
      if (v !== undefined) found = v;
    }
    return coerce(found);
  } catch {
    return false;
  }
}
